<template>
  <b-container fluid>
    <page-header :title="`Configure Asset Events for Asset ID: ${configEvent.mcevAssetId} (Version: ${configEvent.mcevVersion})`">
      <template #actions>
        <b-button @click="$router.go(-1)" variant="outline-secondary" size="sm">
          <i class="ri-arrow-go-back-line"></i> Back
        </b-button>
        <b-button @click="openDuplicateForm" variant="outline-info" size="sm">
          <i class="ri-file-copy-2-line"></i> Duplicate From...
        </b-button>
        <b-button @click="fetchData" :disabled="isLoading" variant="outline-primary" size="sm">
          <i class="ri-refresh-line"></i> Refresh
        </b-button>
      </template>
    </page-header>
    <div class="iq-card px-3">
      <div class="iq-card-body">
        <!-- Tampilan Loading Utama -->
        <div v-if="isLoading" class="text-center text-primary my-2">
          <wave-loader size="lg">Loading data...</wave-loader>
        </div>

        <!-- Konten Utama: Dual Listbox -->
        <b-row v-else class="align-items-center">
          <!-- KOLOM KIRI: Event yang Tersedia -->
          <b-col md="5">
            <b-card>
              <h6 class="card-subtitle mb-2 text-muted">Available Events</h6>
              <b-list-group class="event-list">
                <b-list-group-item
                  v-for="event in availableEvents"
                  :key="event.mevtId"
                  @click="toggleSelection('available', event)"
                  :active="isSelected('available', event)"
                  href="#"
                  class="d-flex justify-content-between align-items-center"
                >
                  {{ event.mevtType }}
                  <b-badge v-if="event.mevtIsPriority == 1" variant="danger">High</b-badge>
                </b-list-group-item>
                <b-list-group-item v-if="!availableEvents.length" class="text-center text-muted">
                  No more events available.
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </b-col>

          <!-- KOLOM TENGAH: Tombol Aksi -->
          <b-col md="2" class="text-center">
            <b-button-group vertical>
              <b-button @click="moveEvents('toSelected')" :disabled="isMoving || tempAvailable.length === 0" variant="primary" class="mb-2">
                <i class="ri-arrow-right-s-line"></i> Add
              </b-button>
              <b-button @click="moveEvents('toAvailable')" :disabled="isMoving || tempSelected.length === 0" variant="outline-danger">
                Remove <i class="ri-arrow-left-s-line"></i>
              </b-button>
            </b-button-group>
            <div v-if="isMoving" class="text-center mt-2">
              <wave-loader />
            </div>
          </b-col>

          <!-- KOLOM KANAN: Event yang Terpilih -->
          <b-col md="5">
            <b-card>
              <h6 class="card-subtitle mb-2 text-muted">Selected Events</h6>
              <b-list-group class="event-list">
                <b-list-group-item
                  v-for="event in selectedEvents"
                  :key="event.asevId"
                  @click="toggleSelection('selected', event)"
                  :active="isSelected('selected', event)"
                  href="#"
                   class="d-flex justify-content-between align-items-center"
                >
                  {{ event.eventDetails.mevtType }}
                   <b-badge v-if="event.eventDetails.mevtIsPriority == 1" variant="danger">High</b-badge>
                </b-list-group-item>
                <b-list-group-item v-if="!selectedEvents.length" class="text-center text-muted">
                  No events selected.
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </b-col>
        </b-row>
      </div>

      <!-- Modal untuk Duplikasi -->
      <b-modal
        v-model="displayDuplicateForm"
        title="Duplicate from another Configuration"
        hide-footer
        no-close-on-esc
        no-close-on-backdrop
        size="lg"
      >
        <div role="group" class="form-group mb-3 col-md-12 px-0">
          <label class="d-block form-label text-left">
            Source Configuration:
          </label>
          <v-select
            v-model="duplicateSource"
            :options="otherConfigs"
            label="label"
            placeholder="Select a configuration to duplicate from"
            style="width: 100%;"
          >
          </v-select>
        </div>

        <!-- Bagian Preview yang akan muncul setelah memilih -->
        <div v-if="duplicateSource" class="px-0 col-md-12">
          <h6 class="text-muted">Preview of Events:</h6>
          <b-card class="preview-box">
            <div v-if="isPreviewLoading" class="text-center">
              <wave-loader size="sm">Loading preview...</wave-loader>
            </div>
            <ul v-else-if="previewedEvents.length > 0" class="list-unstyled mb-0">
              <li v-for="event in previewedEvents" :key="event.asevId">
                - {{ getEventName(event.asevMevtId) }}
              </li>
            </ul>
            <div v-else class="text-muted">
              No events found in this configuration.
            </div>
          </b-card>
        </div>

        <div class="form-group d-flex justify-content-end mt-3">
          <b-button @click="displayDuplicateForm = false" class="mr-1" size="sm" variant="outline-danger">
            Cancel
          </b-button>
          <b-button
            @click="onConfirmDuplicate"
            :disabled="!duplicateSource || isDuplicating"
            variant="primary"
            size="sm"
            class="ml-1"
          >
            <wave-loader v-if="isDuplicating" size="sm" mono />
            Confirm Duplicate
          </b-button>
        </div>
      </b-modal>
    </div>
  </b-container>
</template>

<style scoped>
.event-list {
  height: 400px;
  overflow-y: auto;
}
.list-group-item {
  cursor: pointer;
}
.preview-box {
  max-height: 200px;
  overflow-y: auto;
  background-color: var(--mz-surface-2);
}
</style>

<script>
import vSelect from 'vue-select';
import AssetEventSvc from '@/services/AssetEventSvc';
import EventSvc from '@/services/EventSvc';
import ConfigEventSvc from '@/services/ConfigEventSvc';

export default {
  name: 'MasterAssetEvent',
  components: {
    vSelect,
  },
  data() {
    return {
      configId: this.$route.params.id || 0,
      configEvent: {},
      allMasterEvents: [],
      selectedEvents: [],
      tempAvailable: [],
      tempSelected: [],
      isLoading: false,
      isMoving: false,

      // State untuk duplikasi
      displayDuplicateForm: false,
      isDuplicating: false,
      otherConfigs: [],
      duplicateSource: null,

      // State untuk preview
      isPreviewLoading: false,
      previewedEvents: [],
    };
  },
  computed: {
    availableEvents() {
      const selectedEventIds = new Set(this.selectedEvents.map((e) => e.asevMevtId));
      return this.allMasterEvents.filter((event) => !selectedEventIds.has(event.mevtId));
    },
  },
  watch: {
    async duplicateSource(newSource) {
      if (!newSource) {
        this.previewedEvents = [];
        return;
      }

      this.isPreviewLoading = true;
      try {
        const response = await AssetEventSvc.get({
          'finds[asevMcevId]': newSource.value,
          max: 1000,
        });
        this.previewedEvents = response.data;
      } catch (e) {
        console.error('Failed to fetch preview data:', e);
        this.$toast.error('Could not load preview.');
        this.previewedEvents = [];
      } finally {
        this.isPreviewLoading = false;
      }
    },
  },
  methods: {
    async fetchData() {
      this.isLoading = true;
      try {
        const configResponse = await ConfigEventSvc.getById(this.configId);
        this.configEvent = configResponse?.data?.[0] || {};

        const masterEventsResponse = await EventSvc.get({ max: 1000 });
        this.allMasterEvents = masterEventsResponse?.data || [];

        const assetEventsResponse = await AssetEventSvc.get({
          'finds[asevMcevId]': this.configId,
          max: 1000,
        });
        const assetEvents = assetEventsResponse?.data || [];

        this.selectedEvents = assetEvents.map((assetEvent) => {
          const eventDetails = this.allMasterEvents.find((master) => master.mevtId === assetEvent.asevMevtId);
          return { ...assetEvent, eventDetails: eventDetails || {} };
        });
      } catch (e) {
        console.error('Failed to fetch data:', e);
        this.$toast.error('Could not load configuration data.');
      } finally {
        this.isLoading = false;
      }
    },

    toggleSelection(type, event) {
      const targetArray = type === 'available' ? this.tempAvailable : this.tempSelected;
      const eventId = type === 'available' ? event.mevtId : event.asevId;

      const index = targetArray.indexOf(eventId);
      if (index > -1) {
        targetArray.splice(index, 1);
      } else {
        targetArray.push(eventId);
      }
    },

    isSelected(type, event) {
      const targetArray = type === 'available' ? this.tempAvailable : this.tempSelected;
      const eventId = type === 'available' ? event.mevtId : event.asevId;
      return targetArray.includes(eventId);
    },

    async moveEvents(direction) {
      this.isMoving = true;
      try {
        if (direction === 'toSelected') {
          const createPromises = this.tempAvailable.map((mevtId) => {
            const payload = { asevMcevId: this.configId, asevMevtId: mevtId };
            return AssetEventSvc.create(payload);
          });
          await Promise.all(createPromises);
          this.$toast.success(`${this.tempAvailable.length} event(s) added.`);
          this.tempAvailable = [];
        } else if (direction === 'toAvailable') {
          const deletePromises = this.tempSelected.map((asevId) => AssetEventSvc.delete(asevId));
          await Promise.all(deletePromises);
          this.$toast.success(`${this.tempSelected.length} event(s) removed.`);
          this.tempSelected = [];
        }
        await this.fetchData();
      } catch (e) {
        console.error('Error moving events:', e);
        this.$toast.error('An error occurred. Please try again.');
      } finally {
        this.isMoving = false;
      }
    },

    async openDuplicateForm() {
      this.duplicateSource = null;
      try {
        const response = await ConfigEventSvc.get({ max: 1000 });
        this.otherConfigs = response.data
          .filter((config) => config.mcevId !== this.configId)
          .map((config) => ({
            label: `Asset ID: ${config.mcevAssetId} (Version: ${config.mcevVersion})`,
            value: config.mcevId,
          }));
        this.displayDuplicateForm = true;
      } catch (e) {
        this.$toast.error('Failed to load other configurations.');
      }
    },

    async onConfirmDuplicate() {
      if (!this.duplicateSource) return;

      this.isDuplicating = true;
      try {
        const sourceEvents = this.previewedEvents;
        if (!sourceEvents || sourceEvents.length === 0) {
          this.$toast.info('Source configuration has no events to duplicate.');
          return;
        }

        const currentEventIds = new Set(this.selectedEvents.map((e) => e.asevMevtId));
        const eventsToCreate = sourceEvents.filter((event) => !currentEventIds.has(event.asevMevtId));
        if (eventsToCreate.length === 0) {
          this.$toast.info('All events from the source already exist in this configuration.');
          return;
        }

        const createPromises = eventsToCreate.map((event) => {
          const payload = { asevMcevId: this.configId, asevMevtId: event.asevMevtId };
          return AssetEventSvc.create(payload);
        });
        await Promise.all(createPromises);

        this.$toast.success(`${eventsToCreate.length} event(s) have been duplicated successfully.`);
        await this.fetchData();
      } catch (e) {
        console.error('Duplication error:', e);
        this.$toast.error('An error occurred during duplication.');
      } finally {
        this.isDuplicating = false;
        this.displayDuplicateForm = false;
      }
    },

    getEventName(eventId) {
      const event = this.allMasterEvents.find((e) => e.mevtId === eventId);
      return event ? event.mevtType : 'Unknown Event';
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>
